import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { booksService } from '../api/booksService';
import type { BookSearchResultDto, CrossBookSearchResultDto, SearchResultDto } from '../types/search';
import SearchBar from '../components/SearchBar';
import Layout from '../components/Layout';

/**
 * Search results page - displays search results for in-book or cross-book search
 * Mobile-first responsive design with touch-friendly result cards
 */
export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get('q') || '';
  const bookId = searchParams.get('bookId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inBookResults, setInBookResults] = useState<BookSearchResultDto | null>(null);
  const [crossBookResults, setCrossBookResults] = useState<CrossBookSearchResultDto | null>(null);

  useEffect(() => {
    if (!query) {
      return;
    }

    const performSearch = async () => {
      setLoading(true);
      setError(null);
      setInBookResults(null);
      setCrossBookResults(null);

      try {
        if (bookId) {
          // In-book search
          const results = await booksService.searchInBook(bookId, query);
          setInBookResults(results);
        } else {
          // Cross-book search
          const results = await booksService.searchAcrossBooks(query);
          setCrossBookResults(results);
        }
      } catch (err: any) {
        console.error('Search failed:', err);
        setError(err.message || 'Search failed. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query, bookId]);

  const handleResultClick = (result: SearchResultDto) => {
    // Navigate to the book reader with the specific chapter/page
    if (result.pageNumber !== null) {
      // PDF - navigate to specific page
      navigate(`/read/${result.bookId}?page=${result.pageNumber}`);
    } else if (result.chapterIndex !== null) {
      // EPUB - navigate to specific chapter
      navigate(`/read/${result.bookId}?chapter=${result.chapterIndex}`);
    }
  };

  const highlightText = (text: string, searchQuery: string): React.ReactNode => {
    if (!searchQuery) return text;

    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <mark key={index} className="bg-warning-200 font-semibold">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const renderSearchResult = (result: SearchResultDto, bookTitle: string) => (
    <button
      type="button"
      key={`${result.bookId}-${result.pageNumber || result.chapterIndex}`}
      onClick={() => handleResultClick(result)}
      aria-label={`Go to ${result.chapterTitle || (result.pageNumber !== null ? `page ${result.pageNumber}` : `chapter ${(result.chapterIndex || 0) + 1}`)}, ${bookTitle}`}
      className="p-4 bg-white border border-neutral-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer text-left w-full"
    >
      {/* Location info */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-neutral-900">
          {result.pageNumber !== null
            ? `Page ${result.pageNumber}`
            : result.chapterTitle || `Chapter ${(result.chapterIndex || 0) + 1}`}
        </span>
        <span className="text-sm text-neutral-500">
          ({result.totalMatchesInLocation} {result.totalMatchesInLocation === 1 ? 'match' : 'matches'})
        </span>
      </div>

      {/* Context snippets */}
      <div className="space-y-2">
        {result.occurrences.slice(0, 3).map((occurrence, idx) => (
          <p key={idx} className="text-sm text-neutral-700 leading-relaxed">
            {highlightText(occurrence.context, query)}
          </p>
        ))}
        {result.occurrences.length > 3 && (
          <p className="text-sm text-neutral-500 italic">
            +{result.occurrences.length - 3} more {result.occurrences.length - 3 === 1 ? 'match' : 'matches'}
          </p>
        )}
      </div>
    </button>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        {/* Search bar */}
        <div className="mb-6">
          <SearchBar initialQuery={query} bookId={bookId} placeholder="Search..." />
        </div>

        {/* Results header */}
        {!loading && !error && (inBookResults || crossBookResults) && (
          <div className="mb-6">
            {inBookResults && (
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 mb-2">
                  Search results in "{inBookResults.bookTitle}"
                </h1>
                <p className="text-neutral-600">
                  Found {inBookResults.totalMatches} {inBookResults.totalMatches === 1 ? 'match' : 'matches'} for "{query}"
                </p>
              </div>
            )}

            {crossBookResults && (
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 mb-2">
                  Search results across your library
                </h1>
                <p className="text-neutral-600">
                  Found {crossBookResults.totalMatches} {crossBookResults.totalMatches === 1 ? 'match' : 'matches'} in {crossBookResults.totalBooks} {crossBookResults.totalBooks === 1 ? 'book' : 'books'} for "{query}"
                  {crossBookResults.searchTimeMs && (
                    <span className="text-sm text-neutral-500 ml-2">({crossBookResults.searchTimeMs}ms)</span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-neutral-600">Searching...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="p-4 bg-error-50 border border-error-200 rounded-lg">
            <p className="text-error-800 font-medium">Error</p>
            <p className="text-error-600 text-sm mt-1">{error}</p>
          </div>
        )}

        {/* In-book search results */}
        {!loading && !error && inBookResults && (
          <div className="space-y-4">
            {inBookResults.results.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-16 h-16 mx-auto text-neutral-400 mb-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
                <p className="text-neutral-600 text-lg">No results found for "{query}"</p>
                <p className="text-neutral-500 text-sm mt-2">Try a different search term</p>
              </div>
            ) : (
              inBookResults.results.map((result) => renderSearchResult(result, inBookResults.bookTitle))
            )}
          </div>
        )}

        {/* Cross-book search results */}
        {!loading && !error && crossBookResults && (
          <div className="space-y-8">
            {crossBookResults.bookResults.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-16 h-16 mx-auto text-neutral-400 mb-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
                <p className="text-neutral-600 text-lg">No results found for "{query}"</p>
                <p className="text-neutral-500 text-sm mt-2">Try a different search term</p>
              </div>
            ) : (
              crossBookResults.bookResults.map((bookResult) => (
                <div key={bookResult.bookId} className="space-y-4">
                  {/* Book header */}
                  <div className="flex items-center gap-3 pb-3 border-b border-neutral-200">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 text-primary-600"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                      />
                    </svg>
                    <div>
                      <h2 className="text-lg font-semibold text-neutral-900">{bookResult.bookTitle}</h2>
                      <p className="text-sm text-neutral-500">
                        {bookResult.totalMatches} {bookResult.totalMatches === 1 ? 'match' : 'matches'}
                      </p>
                    </div>
                  </div>

                  {/* Book results */}
                  <div className="space-y-4">
                    {bookResult.results.map((result) => renderSearchResult(result, bookResult.bookTitle))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
